import type { ChartData, QuickChartData } from '@trinity/chat';

export type ValueWithUnit = {
  value: number | string;
  unit?: string;
  isPrefix?: boolean;
};

export function formatValueWithUnit({ value, unit, isPrefix }: ValueWithUnit): string {
  if (!unit) return String(value);
  return isPrefix ? `${unit}${value}` : `${value}${unit}`;
}

export function extractUnit(val: string | number): { unit: string; isPrefix: boolean } | null {
  if (typeof val !== 'string') return null;

  const trimmed = val.trim();

  // Check for suffix units (%, °, etc.)
  if (trimmed.endsWith('%')) {
    return { unit: '%', isPrefix: false };
  }
  if (trimmed.endsWith('°')) {
    return { unit: '°', isPrefix: false };
  }

  // Check for prefix units ($, €, £, ¥, etc.)
  const prefixMatches = trimmed.match(/^([\$€£¥₹]+)/);
  if (prefixMatches) {
    return { unit: prefixMatches[1], isPrefix: true };
  }

  return null;
}

export function detectUnitFromRecords(records: (string | number)[][]): { unit: string; isPrefix: boolean } | null {
  for (const row of records) {
    for (const val of row) {
      const detected = extractUnit(val);
      if (detected) {
        return detected;
      }
    }
  }
  return null;
}

export function chatDataToQuickChartData(chartData: ChartData): QuickChartData | null {
  const { type } = chartData.chart;
  const chartPayload = chartData.chart.chart;

  switch (type) {
    case 'bar':
      return { type: 'bar', chart: chartPayload as Extract<QuickChartData, { type: 'bar' }>['chart'] };
    case 'stacked-bar':
      return { type: 'stacked-bar', chart: chartPayload as Extract<QuickChartData, { type: 'stacked-bar' }>['chart'] };
    case 'pie':
      return { type: 'pie', chart: chartPayload as Extract<QuickChartData, { type: 'pie' }>['chart'] };
    case 'multiple-clustered-bar':
      return { type: 'multiple-clustered-bar', chart: chartPayload as Extract<QuickChartData, { type: 'multiple-clustered-bar' }>['chart'] };
    case 'multiple-stacked-bar':
      return { type: 'multiple-stacked-bar', chart: chartPayload as Extract<QuickChartData, { type: 'multiple-stacked-bar' }>['chart'] };
    default:
      return null;
  }
}

/**
 * Chart types that use Nivo's Responsive* components and therefore require
 * an explicit-height parent container to render correctly.
 */
export const NIVO_CHART_TYPES = new Set<QuickChartData['type']>([
  'bar',
  'stacked-bar',
  'pie',
  'multiple-clustered-bar',
  'multiple-stacked-bar',
]);
